// app/javascript/components/shared/ApiTokenManagerCard.tsx
import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  KeyRound, Copy, Check, Eye, EyeOff, RefreshCw, Terminal,
  FileCode2, ShieldAlert, Sparkles
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Props {
  apiToken?: string | null
  regenerateUrl: string
  title?: string
  description?: string
}

export function ApiTokenManagerCard({
  apiToken,
  regenerateUrl,
  title = 'API Access & Developer Token',
  description = 'Authenticate agentic workflows, scripts, and API requests to PrepKiya.'
}: Props) {
  const { toast } = useToast()
  const [revealed, setRevealed] = useState(false)
  const [copiedType, setCopiedType] = useState<'raw' | 'markdown' | 'curl' | null>(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  const tokenValue = apiToken || ''
  const hasToken = tokenValue.length > 0

  const copyToClipboard = async (text: string, type: 'raw' | 'markdown' | 'curl', message: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedType(type)
      toast({ title: 'Copied to clipboard', description: message })
      setTimeout(() => setCopiedType(null), 2500)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Please copy the token manually.',
        variant: 'destructive'
      })
    }
  }

  const handleCopyRaw = () => {
    if (!hasToken) return
    copyToClipboard(tokenValue, 'raw', 'API Token copied!')
  }

  const handleCopyMarkdown = () => {
    if (!hasToken) return
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://prepkiya.com'
    const mdSnippet = `### PrepKiya API Token
\`\`\`http
Authorization: Bearer ${tokenValue}
\`\`\`

#### cURL Example:
\`\`\`bash
curl -X GET "${origin}/api/v1/standards" \\
  -H "Authorization: Bearer ${tokenValue}" \\
  -H "Content-Type: application/json"
\`\`\`
`
    copyToClipboard(mdSnippet, 'markdown', 'API Token markdown block copied!')
  }

  const handleCopyCurl = () => {
    if (!hasToken) return
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://prepkiya.com'
    const curlCommand = `curl -X GET "${origin}/api/v1/standards" \\\n  -H "Authorization: Bearer ${tokenValue}" \\\n  -H "Content-Type: application/json"`
    copyToClipboard(curlCommand, 'curl', 'cURL command copied!')
  }

  const handleRegenerate = () => {
    setRegenerating(true)
    router.post(regenerateUrl, {}, {
      preserveScroll: true,
      onSuccess: () => {
        setConfirmModalOpen(false)
        setRevealed(true)
        toast({
          title: 'Success',
          description: hasToken ? 'API Token regenerated successfully.' : 'API Token created successfully.'
        })
      },
      onFinish: () => setRegenerating(false)
    })
  }

  return (
    <Card className="rounded-2xl border border-border bg-card shadow-glass-sm overflow-hidden">
      <CardHeader className="p-4 sm:p-5 border-b border-border/40 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base sm:text-lg font-extrabold flex items-center gap-2 text-foreground">
              <KeyRound className="h-5 w-5 text-primary shrink-0" />
              <span>{title}</span>
            </CardTitle>
            <CardDescription className="text-xs text-muted-foreground">
              {description}
            </CardDescription>
          </div>
          <Badge
            variant={hasToken ? 'outline' : 'secondary'}
            className={`font-semibold text-[11px] ${
              hasToken ? 'border-primary/40 text-primary bg-primary/10' : ''
            }`}
          >
            {hasToken ? 'Active Token' : 'No Token'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Token Input & Main Actions */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-foreground flex items-center justify-between">
            <span>Your Personal API Token</span>
            {hasToken && (
              <span className="text-[11px] font-normal text-muted-foreground">
                Keep this secret. Never expose it in public repos.
              </span>
            )}
          </label>

          {hasToken ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative flex-1">
                <Input
                  type={revealed ? 'text' : 'password'}
                  readOnly
                  value={tokenValue}
                  className="pr-10 font-mono text-xs h-10 rounded-xl bg-background border-input select-all"
                />
                <button
                  type="button"
                  onClick={() => setRevealed(!revealed)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded-md transition-colors"
                  title={revealed ? 'Hide Token' : 'Reveal Token'}
                >
                  {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyRaw}
                  className="h-10 rounded-xl text-xs font-bold px-3 border-border hover:bg-muted/50 cursor-pointer"
                  title="Copy raw token"
                >
                  {copiedType === 'raw' ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1.5 text-primary" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                    </>
                  )}
                </Button>

                <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10 rounded-xl text-xs font-bold px-3 border-destructive/40 text-destructive hover:bg-destructive/10 cursor-pointer"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-destructive">
                        <ShieldAlert className="h-5 w-5" /> Regenerate API Token?
                      </DialogTitle>
                      <DialogDescription className="text-xs text-muted-foreground pt-2">
                        Regenerating will immediately <strong className="text-foreground">invalidate your existing token</strong>.
                        Any active scripts, agent workflows, or external tools using the current token will stop working until updated.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="pt-3 gap-2 sm:gap-0">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setConfirmModalOpen(false)}
                        disabled={regenerating}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleRegenerate}
                        disabled={regenerating}
                      >
                        {regenerating ? 'Regenerating…' : 'Yes, Regenerate Token'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-xl border border-dashed border-border bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-center sm:text-left">
                <p className="text-xs font-bold text-foreground">No API token generated yet</p>
                <p className="text-[11px] text-muted-foreground">
                  Generate a secure 64-character token to start accessing PrepKiya REST endpoints.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleRegenerate}
                disabled={regenerating}
                className="h-9 rounded-xl font-bold text-xs bg-primary text-primary-fg shadow-xs cursor-pointer shrink-0"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {regenerating ? 'Generating…' : 'Generate API Token'}
              </Button>
            </div>
          )}
        </div>

        {/* Formatted Copy Snippets Bar */}
        {hasToken && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-[11px] font-bold text-muted-foreground mr-1">Copy as:</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyMarkdown}
              className="h-7 rounded-lg text-[11px] font-semibold px-2.5 bg-background hover:bg-muted/60 border-border cursor-pointer gap-1.5"
            >
              {copiedType === 'markdown' ? (
                <Check className="h-3 w-3 text-primary" />
              ) : (
                <FileCode2 className="h-3 w-3 text-secondary" />
              )}
              <span>Markdown Snippet</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyCurl}
              className="h-7 rounded-lg text-[11px] font-semibold px-2.5 bg-background hover:bg-muted/60 border-border cursor-pointer gap-1.5"
            >
              {copiedType === 'curl' ? (
                <Check className="h-3 w-3 text-primary" />
              ) : (
                <Terminal className="h-3 w-3 text-tertiary" />
              )}
              <span>cURL Request</span>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
